import { getCookieData, useTimer } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { useToast } from '@repo/ui';
import { pascalCase } from 'es-toolkit';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { CertType } from '@/__generated__/simpleAuthConfirmRequestTokenMutation.graphql';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { setIndividualRefundData } from '@/components/tax-refund/common/data/individual-refund-data';
import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY } from '@/components/tax-refund/common/data/result-refund-data';
import { TAX_REFUND_ERROR } from '@/components/tax-refund/common/error/error';
import {
  SIMPLE_AUTH_TIMEOUT_TOAST_TITLE,
  SimpleAuthConfirmCTA,
  SimpleAuthenticationGuide,
  SimpleAuthRetrySection
} from '@/components/tax-refund/simple-auth/confirm/ContentItem';
import { useSimpleAuthDialog } from '@/components/tax-refund/simple-auth/confirm/Dialogs';
import { shouldSendActionLog } from '@/components/tax-refund/simple-auth/input/SimpleAuthInputContainer';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import { useRequestHometaxRefundToken, useRequestSimpleAuthConfirmToken } from '@/lib/hooks/use-hometax-simple-auth';
import { useFromTypeStaticData } from '@/lib/hooks/use-simple-auth-from-type';
import {
  refundTokenAtom,
  simpleAuthCertTypeAtom,
  simpleAuthCredentialAtom,
  simpleAuthFromTypeAtom,
  simpleAuthUserIdentityAtom
} from '@/lib/stores/simple-auth';

export const SimpleAuthConfirmContainer = () => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const fromType = useAtomValue(simpleAuthFromTypeAtom);
  const certType = useAtomValue(simpleAuthCertTypeAtom);
  const userInfo = useAtomValue(simpleAuthUserIdentityAtom);
  const credentials = useAtomValue(simpleAuthCredentialAtom);
  const setRefundToken = useSetAtom(refundTokenAtom);
  const { requestSimpleAuthConfirmToken } = useRequestSimpleAuthConfirmToken();
  const { requestRefundToken } = useRequestHometaxRefundToken(fromType);
  const { showDialogNoHometaxUserError, showDialogNotEqualTinError, showDialogHometaxSessionError, showDialogAlreadyCompleted, showDialogAlreadyApplied } =
    useSimpleAuthDialog();
  const { getConfirmPageEvents, getConfirmNextPath } = useFromTypeStaticData(fromType);
  const [isProcessing, setIsProcessing] = useState(false);
  const timer = useTimer(5, 0);
  const { toast } = useToast();
  const events = getConfirmPageEvents();
  const { commit: saveActionLog } = useSaveRefundActionLog();

  const sendActionCode = useCallback(() => {
    const refundData = getIndisSearchRefundData();
    const actionCode = router.query?.actionCode ?? getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);

    if (shouldSendActionLog(fromType, actionCode)) {
      saveActionLog({
        variables: {
          actionCode,
          refundId: refundData?.refundId,
          actionStatus: '간편인증완료'
        }
      });
    }
  }, [router.query?.actionCode, fromType, saveActionLog]);

  const handleConfirmClick = useCallback(async () => {
    if (isProcessing || !userInfo || !credentials) return;
    if (timer.isTimeOut) {
      toast({ variant: 'warning', title: SIMPLE_AUTH_TIMEOUT_TOAST_TITLE });
      return;
    }

    setIsProcessing(true);
    sendClickEvent(events.clickCta, { type: certType });

    try {
      const { birthday, name, phone } = userInfo;
      const { reqTxId, token, cxId } = credentials;
      if (Object.values(userInfo).length === 0) {
        toast({ variant: 'warning', title: '입력한 정보가 존재하지 않습니다.' });
        router.back();
        return;
      }
      const simpleAuthToken = await requestSimpleAuthConfirmToken({
        name,
        phone,
        birthday,
        cxId,
        reqTxId,
        token,
        certType: pascalCase(certType) as CertType
      });
      const refundToken = await requestRefundToken({
        corpType: 'INDIS',
        loginType: pascalCase(certType) as CertType,
        token: (simpleAuthToken as string) ?? ''
      });
      if (refundToken) {
        // 인증 이후 수집 api 에서 사용되는 refundToken
        setRefundToken(refundToken);
        setIndividualRefundData({
          corpType: 'INDIS',
          authType: 'simple-auth',
          serviceAgreementFlow: false,
          // 삭제예정 - 더이상 해당 객체의 refundToken이 사용되지 않음
          refundToken: ''
        });

        // 사용자 행동 패턴 파악용 actionCode
        sendActionCode();

        router.replace({
          pathname: getConfirmNextPath(),
          query: { type: 'indis' }
        });
        return;
      }
    } catch (errors: unknown) {
      const { error, data } = (errors ?? {}) as {
        error?: { code?: string; message?: string };
        data?: {
          simpleAuthConfirmToken?: null;
          lastLoginType?: string | null;
          email?: string | null;
        };
      };

      if (!!error?.code && data?.simpleAuthConfirmToken === null) {
        // 인증 미완료 Case
        toast({ variant: 'warning', title: `${certType === 'kakao' ? '카카오' : '네이버'}앱에서 인증을 완료해 주세요.` });
        return;
      }
      switch (error?.code) {
        case TAX_REFUND_ERROR.홈택스세션오류:
          showDialogHometaxSessionError();
          break;
        case TAX_REFUND_ERROR.홈택스간편인증오류:
          toast({ variant: 'warning', title: error?.message ?? '홈택스 간편인증 오류' });
          break;
        case TAX_REFUND_ERROR.홈택스회원아님:
          showDialogNoHometaxUserError();
          break;
        case TAX_REFUND_ERROR.기존TIN과불일치: {
          const searchRefundWebStorageData = getIndisSearchRefundData();
          showDialogNotEqualTinError({ bizName: searchRefundWebStorageData?.bizName ?? '' });
          break;
        }
        case TAX_REFUND_ERROR.환급완료유저TIN:
          if (data?.lastLoginType) {
            showDialogAlreadyApplied({
              email: data?.email ?? '',
              loginType: data?.lastLoginType ?? ''
            });
            break;
          }
          showDialogAlreadyCompleted();
          break;
        default:
          toast({ variant: 'warning', title: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.' });
      }
    } finally {
      setIsProcessing(false);
    }
  }, [
    certType,
    credentials,
    events.clickCta,
    getConfirmNextPath,
    isProcessing,
    requestRefundToken,
    requestSimpleAuthConfirmToken,
    router,
    sendActionCode,
    sendClickEvent,
    setRefundToken,
    showDialogAlreadyApplied,
    showDialogAlreadyCompleted,
    showDialogHometaxSessionError,
    showDialogNoHometaxUserError,
    showDialogNotEqualTinError,
    timer.isTimeOut,
    toast,
    userInfo
  ]);

  return (
    <div className={'page-padding flex flex-col flex-1 pb-[92px]'}>
      <SimpleAuthenticationGuide
        onClickCheckForm={() => {
          sendClickEvent(events.clickCheckForm, { type: certType });
          router.back();
        }}
      />
      <SimpleAuthRetrySection min={timer.min} sec={timer.sec} restart={timer.restart} isTimeOut={timer.isTimeOut} />
      <SimpleAuthConfirmCTA isLoading={isProcessing} onClick={handleConfirmClick} />
    </div>
  );
};
